/**
 * Header: navigation submenus, utility flyovers (search, cart, mobile menu)
 * and the cart badge.
 *
 * One controller owns the flyover panel under the bar. Its state is one of
 *   closed | opening | open | swapping | closing
 * and every open, swap and close is an operation with a token: async steps
 * re-check the token before they touch the DOM, so a stale hover can never
 * write its content or height over a newer one.
 *
 * Timings live in the CSS on the elements (the Tailwind duration classes in
 * sections/header.liquid). The controller waits for the real transitionend,
 * with a timeout fallback read from the same CSS, instead of guessing.
 *
 * Pointer intent:
 *   open  - the pointer rests on an item for REST_MS, then the panel opens
 *           (OPEN_DELAY_MS adds a further pause if one is ever wanted)
 *   swap  - the pointer rests on another item for REST_MS; while the pointer
 *           is heading down into the open panel (inside the "safe triangle"
 *           between its last position and the panel's top corners) the swap
 *           is held for AIM_HOLD_MS and re-checked
 *   close - leaving the header, entering the logo or utility area or the
 *           backdrop, scrolling, Escape, the close button
 *
 * Text content appears as soon as the panel starts to move; images inside a
 * panel (rich-menu image, cart thumbnails) reveal only after the panel has
 * settled at its final height and the image has loaded.
 *
 * The inline handlers in sections/header.liquid call the functions exported
 * at the bottom; cart.js and predictive-search.js call
 * window.updateSubmenuHeight after they change panel content.
 */
(() => {
  const REST_MS = 30; // pointer must rest this long on an item
  const OPEN_DELAY_MS = 0; // extra pause before a closed panel starts to open
  const AIM_HOLD_MS = 200; // hold a swap while the pointer heads into the panel
  const AIM_MAX_HOLDS = 2; // then swap anyway
  const HEIGHT_DEBOUNCE_MS = 20;
  const TRANSITION_SLACK_MS = 60; // fallback if transitionend never fires

  /* --------------------------
     Elements (queried live so the theme editor can replace the section)
  ---------------------------*/
  const els = {
    get header() {
      return document.querySelector("[data-navbar]");
    },
    get container() {
      return document.getElementById("submenu-container");
    },
    get content() {
      return document.getElementById("submenu-content");
    },
    get overlay() {
      return document.getElementById("overlay");
    },
    get utility() {
      return document.querySelector("[data-utility-container]");
    },
  };

  const headerHeight = () => els.header?.offsetHeight || 0;
  // ~100dvh minus the header; window.innerHeight tracks mobile browser chrome
  const capPx = () => Math.max(0, window.innerHeight - headerHeight());
  const naturalHeight = (root) => {
    if (!root) return 0;
    const el = root.firstElementChild || root;
    return (el?.scrollHeight || 0) + 10;
  };
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  /* --------------------------
     Timing read from the CSS
  ---------------------------*/
  function cssMs(value) {
    // "0.3s, 0.3s" | "150ms" -> largest value in ms
    return (value || "")
      .split(",")
      .map((v) => v.trim())
      .reduce((max, v) => {
        if (!v) return max;
        const ms = v.endsWith("ms") ? parseFloat(v) : parseFloat(v) * 1000;
        return Number.isFinite(ms) ? Math.max(max, ms) : max;
      }, 0);
  }

  function transitionMs(el) {
    const cs = getComputedStyle(el);
    return cssMs(cs.transitionDuration) + cssMs(cs.transitionDelay);
  }

  // Resolves when `property` finishes transitioning on `el`, or after the
  // CSS-declared time plus slack if the browser never fires the event (a
  // hidden tab, a transition skipped because the value did not change).
  function afterTransition(el, property) {
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        el.removeEventListener("transitionend", onEnd);
        clearTimeout(timer);
        resolve();
      };
      const onEnd = (e) => {
        if (e.target === el && e.propertyName === property) finish();
      };
      el.addEventListener("transitionend", onEnd);
      const timer = setTimeout(finish, transitionMs(el) + TRANSITION_SLACK_MS);
    });
  }

  /* --------------------------
     Height tween
     The container transitions max-height (see its classes). `height` is
     pinned to the larger of the two values so the visible height follows
     max-height in both directions, then set to the target when it lands.
  ---------------------------*/
  let tweenId = 0;

  async function tweenHeight(node, fromPx, toPx) {
    if (!node) return;
    const id = ++tweenId;
    const isMobile = window.matchMedia("(max-width: 639px)").matches;
    const fullScreen = isMobile && toPx > 0;
    const toH = fullScreen ? "100dvh" : `${toPx}px`;

    node.classList.toggle("h-dvh", fullScreen);
    if (!fullScreen && fromPx === toPx) {
      node.style.height = toH;
      node.style.maxHeight = toH;
      return;
    }

    node.style.maxHeight = `${fromPx}px`;
    node.style.height = fullScreen ? toH : `${Math.max(fromPx, toPx)}px`;
    void node.offsetHeight; // commit the start value
    const landed = afterTransition(node, "max-height");
    node.style.maxHeight = toH;
    await landed;
    if (id !== tweenId) return; // a newer tween owns the node now
    node.style.height = toH;
  }

  /* --------------------------
     The panel controller
  ---------------------------*/
  const panel = {
    state: "closed",
    active: null, // { type, idx }
    token: 0,
    lastHeightUpdate: Promise.resolve(),
  };

  const sameKey = (a, b) => !!a && !!b && a.type === b.type && a.idx === b.idx;
  const isOpenish = () => panel.state === "open" || panel.state === "swapping" || panel.state === "opening";

  function sourceFor(key) {
    const name = key.type === "utility" ? `utility-${key.idx}` : `${key.type}-${key.idx}`;
    return document.querySelector(`[data-submenu="${name}"]`);
  }

  function showOverlay(overlay) {
    overlay.classList.remove("duration-30");
    overlay.classList.add("delay-40", "duration-150");
    overlay.classList.remove("opacity-0");
    overlay.classList.add("opacity-100");
    overlay.style.pointerEvents = "auto";
  }

  function hideOverlay(overlay) {
    overlay.classList.add("duration-30");
    overlay.classList.remove("delay-40", "duration-150");
    overlay.classList.remove("opacity-100");
    overlay.classList.add("opacity-0");
    overlay.style.pointerEvents = "none";
  }

  // Images in a panel stay invisible until the panel has settled and the
  // image has loaded; the img elements carry their own opacity transition.
  function hideImages(root) {
    root.querySelectorAll("img").forEach((img) => {
      img.style.opacity = "0";
    });
  }

  function whenLoaded(img) {
    if (img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      img.addEventListener("load", resolve, { once: true });
      img.addEventListener("error", resolve, { once: true });
    });
  }

  function revealImages(root, token) {
    root.querySelectorAll("img").forEach(async (img) => {
      await whenLoaded(img);
      if (token !== panel.token || !img.isConnected) return;
      img.style.opacity = "1";
    });
  }

  async function show(key) {
    const source = sourceFor(key);
    const { container, content, overlay } = els;
    if (!source || !container || !content || !overlay) return;

    const token = ++panel.token;
    const wasClosed = panel.state === "closed" || panel.state === "closing";
    panel.active = key;
    panel.state = wasClosed ? "opening" : "swapping";
    updateUtilityButtons(true);

    showOverlay(overlay);
    container.style.pointerEvents = "auto";
    container.setAttribute("aria-hidden", "false");
    container.classList.add("md:overflow-y-hidden");

    if (wasClosed) {
      content.classList.remove("opacity-100");
      if (OPEN_DELAY_MS > 0) {
        await sleep(OPEN_DELAY_MS);
        if (token !== panel.token) return;
      }
    }

    const from = container.getBoundingClientRect().height || 0;
    const target = Math.min(source.scrollHeight, capPx());

    // The new content goes in and starts fading in as the panel begins to
    // move (the panel edge clips it while it grows); only the images wait
    // for the panel to settle.
    content.innerHTML = source.outerHTML;
    hideImages(content);
    const landed = tweenHeight(container, from, target);
    content.classList.add("opacity-100");
    await landed;
    if (token !== panel.token) return;

    revealImages(content, token);
    content.querySelector("[data-autofocus]")?.focus();
    container.classList.remove("md:overflow-y-hidden");
    panel.state = "open";
  }

  // `force` closes even during the opening grace window (Escape, the close
  // button, a pointer entering the logo/utility/backdrop). Event-less
  // requests (scroll, leaving the header) wait for the panel to finish
  // opening, as before.
  async function hide({ force = false } = {}) {
    if (panel.state === "closed" || panel.state === "closing") return;
    if (panel.state === "opening" && !force) return;
    const { container, content, overlay } = els;
    if (!container || !content || !overlay) return;

    const token = ++panel.token;
    panel.state = "closing";
    panel.active = null;
    updateUtilityButtons(false);

    container.style.pointerEvents = "none";
    container.setAttribute("aria-hidden", "true");
    content.classList.remove("opacity-100");
    hideOverlay(overlay);

    const from = container.getBoundingClientRect().height || 0;
    await tweenHeight(container, from, 0);
    if (token !== panel.token) return;
    panel.state = "closed";
  }

  function activate(key, hasLinks) {
    intent.cancel();
    if (!hasLinks) {
      hide({ force: true });
      return;
    }
    if (isOpenish() && sameKey(panel.active, key)) return;
    // A navigation panel replaces an open utility panel and vice versa.
    show(key);
  }

  // Re-measure the open panel after its content changed (cart lines, search
  // results, a rich-menu image). Debounced; resolves when the height lands.
  let heightTimer = null;
  let heightWaiters = [];
  function requestHeightUpdate() {
    return new Promise((resolve) => {
      heightWaiters.push(resolve);
      clearTimeout(heightTimer);
      heightTimer = setTimeout(async () => {
        const waiters = heightWaiters;
        heightWaiters = [];
        const { container, content } = els;
        if (window.innerWidth >= 640 && isOpenish() && container && content) {
          const next = Math.min(naturalHeight(content), capPx());
          const current = container.getBoundingClientRect().height || 0;
          const run = tweenHeight(container, current, next);
          panel.lastHeightUpdate = run;
          await run;
        }
        waiters.forEach((w) => w());
      }, HEIGHT_DEBOUNCE_MS);
    });
  }

  /* --------------------------
     Pointer intent: rest, and the safe triangle for swaps
  ---------------------------*/
  const pointer = { prev: null, cur: null, lastMoveAt: 0 };

  function onPointerMove(e) {
    pointer.prev = pointer.cur;
    pointer.cur = { x: e.clientX, y: e.clientY, t: performance.now() };
    pointer.lastMoveAt = pointer.cur.t;
  }

  function pointInTriangle(p, a, b, c) {
    const s1 = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
    const s2 = (c.x - b.x) * (p.y - b.y) - (c.y - b.y) * (p.x - b.x);
    const s3 = (a.x - c.x) * (p.y - c.y) - (a.y - c.y) * (p.x - c.x);
    const neg = s1 < 0 || s2 < 0 || s3 < 0;
    const pos = s1 > 0 || s2 > 0 || s3 > 0;
    return !(neg && pos);
  }

  // Is the pointer moving from its previous position toward the open panel?
  // The triangle has its apex at the previous position and its base along the
  // panel's top edge; a pointer inside it, and lower than before, is aiming.
  function aimingAtPanel() {
    const { prev, cur } = pointer;
    const container = els.container;
    if (!prev || !cur || !container || !isOpenish()) return false;
    if (cur.t - prev.t > 150 || cur.y <= prev.y) return false;
    const r = container.getBoundingClientRect();
    if (r.height <= 0) return false;
    return pointInTriangle(cur, prev, { x: r.left, y: r.top }, { x: r.right, y: r.top });
  }

  const intent = {
    timer: null,
    item: null,
    cancel() {
      clearTimeout(this.timer);
      this.timer = null;
      this.item = null;
    },
    // Arm an open/swap for `item`; it fires once the pointer has rested on
    // the item, holding while the pointer is aiming at the open panel.
    arm(item, key, hasLinks) {
      this.cancel();
      this.item = item;
      let holds = 0;
      const check = () => {
        if (this.item !== item) return;
        if (!item.matches(":hover")) return this.cancel();
        const swapping = isOpenish() && !sameKey(panel.active, key);
        if (swapping && holds < AIM_MAX_HOLDS && aimingAtPanel()) {
          holds += 1;
          this.timer = setTimeout(check, AIM_HOLD_MS);
          return;
        }
        const rested = performance.now() - pointer.lastMoveAt;
        if (rested < REST_MS) {
          this.timer = setTimeout(check, REST_MS - rested);
          return;
        }
        this.cancel();
        activate(key, hasLinks);
      };
      const delay = isOpenish() && !sameKey(panel.active, key) && aimingAtPanel() ? AIM_HOLD_MS : REST_MS;
      if (delay === AIM_HOLD_MS) holds = 1;
      this.timer = setTimeout(check, delay);
    },
  };

  /* --------------------------
     Utility buttons and rich-menu images
  ---------------------------*/
  function updateUtilityButtons(isActive) {
    const buttons = document.querySelectorAll("[data-utility-button]");
    const closeButton = document.querySelector("[data-close-button]");
    buttons.forEach((btn) => btn.classList.toggle("hidden", isActive));
    if (closeButton) closeButton.classList.toggle("hidden", !isActive);
  }

  function updateRichMenuImage(submenuIdx, linkIdx) {
    const live = els.content?.querySelector(`[data-submenu="rich_menu-${submenuIdx}"]`);
    if (!live) return;
    const link = live.querySelectorAll("a")[linkIdx];
    const img = live.querySelector("img");
    if (!link || !img) return;
    const src = link.getAttribute("data-image");
    if (!src) return;
    if (img.getAttribute("src") === src) return;

    const token = panel.token;
    img.style.opacity = "0";
    img.alt = link.getAttribute("data-title") || "Menu image";
    img.src = src;
    whenLoaded(img).then(async () => {
      // The panel may need to grow for this image; reveal once it has.
      await requestHeightUpdate();
      if (token !== panel.token || !img.isConnected) return;
      img.style.opacity = "1";
    });
  }

  /* --------------------------
     Cart badge
  ---------------------------*/
  function onHeaderCartUpdate() {
    const cartCountBadge = document.querySelector(".cart-count-badge");
    if (!cartCountBadge) return;

    return fetch(`${window.location.pathname}?section_id=cart-icon-count`)
      .then((response) => response.text())
      .then((html) => {
        try {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const newBadge = doc.querySelector(".cart-count-badge");
          if (newBadge) cartCountBadge.replaceWith(newBadge);
        } catch (error) {
          console.error("Error parsing cart count from response:", error);
        }
      })
      .catch((error) => console.error("Error updating cart count:", error));
  }

  /* --------------------------
     Setup: runs on DOMContentLoaded and again whenever the theme editor
     reloads the header section (the elements are replaced, the state is not)
  ---------------------------*/
  let windowBound = false;

  function resetPanel() {
    const { container, overlay } = els;
    if (container) {
      container.style.maxHeight = "0px";
      container.style.height = "0px";
      container.style.pointerEvents = "none";
      container.setAttribute("aria-hidden", "true");
    }
    if (overlay) hideOverlay(overlay);
    panel.token += 1;
    panel.state = "closed";
    panel.active = null;
    intent.cancel();
    updateUtilityButtons(false);
  }

  function init() {
    resetPanel();
    const header = els.header;
    if (header && !header.dataset.headerBound) {
      header.dataset.headerBound = "true";
      header.addEventListener("pointermove", onPointerMove, { passive: true });
      // Keyboard users: focusing a menu item opens its panel. The data-submenu-*
      // attributes sit on the <li>, the focusable <a> is inside it.
      header.addEventListener("focusin", (e) => {
        const menuItem = e.target.closest("[data-submenu-type]");
        if (!menuItem) return;
        const type = menuItem.getAttribute("data-submenu-type");
        const idx = menuItem.getAttribute("data-submenu-idx");
        if (type && idx !== null) {
          activate({ type, idx: parseInt(idx, 10) }, menuItem.getAttribute("data-has-links") === "true");
        }
      });
    }
    if (windowBound) return;
    windowBound = true;
    window.addEventListener(
      "scroll",
      () => {
        if (isOpenish()) hide();
      },
      { passive: true }
    );
    window.addEventListener("resize", () => {
      if (isOpenish()) requestHeightUpdate();
    });
    window.addEventListener("orientationchange", () => {
      if (isOpenish()) requestHeightUpdate();
    });
    // PUB_SUB_EVENTS is a top-level const in constants.js (script scope, not a
    // window property), so test the identifier, not window.
    if (window.subscribe && typeof PUB_SUB_EVENTS !== "undefined") {
      window.subscribe(PUB_SUB_EVENTS.cartUpdate, () => onHeaderCartUpdate());
    }
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", (e) => {
    if (e.target?.querySelector?.("[data-navbar]")) init();
  });

  /* --------------------------
     Public API used by the inline handlers in sections/header.liquid
  ---------------------------*/
  // Pointer (with event) arms intent; keyboard (no event) activates at once.
  window.debouncedActivateSubmenu = function (type, idx, hasLinks, event) {
    const key = { type, idx };
    if (!event || !event.currentTarget) {
      activate(key, hasLinks);
      return;
    }
    onPointerMove(event);
    intent.arm(event.currentTarget, key, hasLinks);
  };

  window.clearHoverTimeout = function () {
    intent.cancel();
  };

  // Called with an event from the logo, utility area and backdrop (mouseenter,
  // focus) and without one from the header's mouseleave, the close button and
  // Escape. A pointer entering the utility area does not close a utility panel.
  window.deactivateSubmenu = function (e) {
    intent.cancel();
    if (!e) {
      hide();
      return;
    }
    const utilityOpen = panel.active?.type === "utility";
    const shouldClose =
      e.type === "mouseleave" ||
      ((e.type === "mouseenter" || e.type === "blur" || e.type === "focus") && !utilityOpen);
    if (shouldClose) hide({ force: true });
  };

  window.toggleUtility = function (utility) {
    const key = { type: "utility", idx: utility };
    const header = els.header;
    const utilityEl = els.utility;
    const opening = !(isOpenish() && sameKey(panel.active, key));
    header?.classList.toggle("data-[scrolled=true]:bg-background/0", !opening);
    utilityEl?.classList.toggle("group-data-[scrolled=true]/header:shadow-md", !opening);
    if (opening) show(key);
    else hide({ force: true });
  };

  window.hoverRichLink = function (submenuIdx, linkIdx) {
    updateRichMenuImage(submenuIdx, linkIdx);
  };

  window.handleMenuKeydown = function (event, type, idx, hasLinks) {
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        activate({ type, idx }, hasLinks);
        break;
      case "Escape":
        hide({ force: true });
        break;
      case "ArrowRight": {
        event.preventDefault();
        const nextItem = event.target.parentElement.nextElementSibling;
        if (nextItem) nextItem.focus();
        break;
      }
      case "ArrowLeft": {
        event.preventDefault();
        const prevItem = event.target.parentElement.previousElementSibling;
        if (prevItem) prevItem.focus();
        break;
      }
    }
  };

  window.updateSubmenuHeight = requestHeightUpdate;
  window.closeSubmenu = () => hide({ force: true });
  // Read-only view of the controller for debugging in devtools.
  window.headerPanelState = () => ({
    state: panel.state,
    active: panel.active,
    token: panel.token,
    intent: intent.item?.dataset?.submenuIdx ?? null,
  });
})();
